import React from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Paper,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Card,
  CardContent,
  CardHeader,
  Switch,
  FormControlLabel,
  Tooltip
} from '@mui/material';
import { 
  Check as CheckIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import LandingHeader from '../components/LandingHeader';
import Footer from '../components/Footer';

const PricingPage: React.FC = () => {
  const [annual, setAnnual] = React.useState(true);
  
  const handleBillingChange = () => {
    setAnnual(!annual);
  };
  
  const pricingPlans = [
    {
      title: 'Free',
      price: {
        monthly: 0,
        annual: 0
      },
      description: 'Basic features for individuals',
      features: [
        'Up to 5 documents',
        'Basic AI document generation',
        'Document editing',
        'PDF and DOCX export',
        'Email support'
      ],
      buttonText: 'Get Started',
      buttonVariant: 'outlined',
      popular: false
    },
    {
      title: 'Professional',
      price: {
        monthly: 29,
        annual: 19
      },
      description: 'Everything you need for professional use',
      features: [
        'Unlimited documents',
        'Advanced AI document generation',
        'Document editing and collaboration',
        'All export formats',
        'Risk analysis and suggestions',
        'Template library access',
        'Priority email support'
      ],
      buttonText: 'Start Free Trial',
      buttonVariant: 'contained',
      popular: true
    },
    {
      title: 'Enterprise',
      price: {
        monthly: 79,
        annual: 59
      },
      description: 'Advanced features for teams and businesses',
      features: [
        'Everything in Professional',
        'Team collaboration',
        'Advanced security features',
        'Custom templates',
        'API access',
        'Integration with CRM systems',
        'Dedicated account manager',
        '24/7 phone and email support'
      ],
      buttonText: 'Contact Sales',
      buttonVariant: 'outlined',
      popular: false
    }
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box sx={{ 
        bgcolor: '#1a365d', 
        color: 'white',
        py: { xs: 8, md: 12 },
        position: 'relative',
        overflow: 'hidden',
        pt: 4
      }}>
        {/* Header */}
        <LandingHeader />
        
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
            <Typography 
              variant="h2" 
              component="h1" 
              fontWeight="800" 
              gutterBottom
              sx={{ 
                fontSize: { xs: '2rem', md: '3rem' },
                lineHeight: 1.2
              }}
            >
              Simple, Transparent Pricing
            </Typography>
            <Typography 
              variant="subtitle1" 
              sx={{ 
                mb: 4, 
                opacity: 0.9,
                fontSize: { xs: '1rem', md: '1.1rem' },
                maxWidth: '700px',
                mx: 'auto'
              }}
            >
              Choose the plan that works best for you. All plans include a 14-day free trial.
            </Typography>
          </Box>
        </Container>
        
        {/* Background decoration */}
        <Box sx={{
          position: 'absolute',
          right: { xs: '-300px', md: '-150px' },
          top: '50%',
          transform: 'translateY(-50%)',
          width: '600px',
          height: '600px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(45,140,255,0.3) 0%, rgba(45,140,255,0) 70%)',
          zIndex: 0
        }} />
      </Box>
      
      {/* Pricing Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 }, mt: { xs: 0, md: -8 } }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <FormControlLabel
            control={
              <Switch 
                checked={annual} 
                onChange={handleBillingChange} 
                color="primary"
                sx={{ mx: 1 }}
              />
            }
            label=""
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography 
              variant="h6" 
              sx={{ 
                color: annual ? 'text.secondary' : '#1a365d',
                fontWeight: annual ? 'normal' : 'bold'
              }}
            >
              Monthly
            </Typography>
            <Box sx={{ width: 80 }} />
            <Typography 
              variant="h6" 
              sx={{ 
                color: annual ? '#1a365d' : 'text.secondary',
                fontWeight: annual ? 'bold' : 'normal'
              }}
            >
              Annual
            </Typography>
            <Tooltip title="Save up to 35% with annual billing" placement="top">
              <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
                <InfoIcon color="primary" fontSize="small" />
              </Box>
            </Tooltip>
          </Box>
          {annual && (
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'success.main', 
                mt: 1,
                fontWeight: 'medium'
              }}
            >
              Save up to 35% with annual billing
            </Typography>
          )}
        </Box>
        
        <Grid container spacing={4} justifyContent="center">
          {pricingPlans.map((plan, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card 
                elevation={0}
                sx={{ 
                  height: '100%',
                  borderRadius: '16px',
                  border: plan.popular ? '2px solid #2d8cff' : '1px solid rgba(0,0,0,0.08)',
                  position: 'relative',
                  transition: 'all 0.3s ease',
                  transform: plan.popular ? 'scale(1.05)' : 'scale(1)',
                  zIndex: plan.popular ? 2 : 1,
                  '&:hover': {
                    boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                    transform: plan.popular ? 'scale(1.07)' : 'scale(1.02)',
                  }
                }}
              >
                {plan.popular && (
                  <Box 
                    sx={{ 
                      position: 'absolute', 
                      top: 12, 
                      right: 12,
                      bgcolor: '#2d8cff',
                      color: 'white',
                      px: 2,
                      py: 0.5,
                      borderRadius: '12px',
                      fontSize: '0.75rem',
                      fontWeight: 'bold'
                    }}
                  >
                    MOST POPULAR
                  </Box>
                )}
                <CardHeader
                  title={
                    <Typography variant="h5" fontWeight="bold" sx={{ color: '#1a365d' }}>
                      {plan.title}
                    </Typography>
                  }
                  subheader={
                    <Typography variant="body2" color="text.secondary">
                      {plan.description}
                    </Typography>
                  }
                  sx={{ pb: 0 }}
                />
                <CardContent>
                  <Box sx={{ textAlign: 'center', py: 3 }}>
                    <Typography 
                      variant="h3" 
                      component="div" 
                      fontWeight="bold"
                      sx={{ color: '#1a365d' }}
                    >
                      ${annual ? plan.price.annual : plan.price.monthly}
                      <Typography 
                        component="span" 
                        variant="body1" 
                        sx={{ 
                          verticalAlign: 'middle', 
                          ml: 1,
                          color: 'text.secondary'
                        }}
                      >
                        /mo
                      </Typography>
                    </Typography>
                    {annual && plan.price.annual > 0 && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Billed annually (${plan.price.annual * 12}/year)
                      </Typography>
                    )}
                  </Box>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <List sx={{ py: 2 }}>
                    {plan.features.map((feature, i) => (
                      <ListItem key={i} sx={{ py: 0.5, px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <CheckIcon sx={{ color: '#2d8cff' }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary={feature} 
                          primaryTypographyProps={{ 
                            variant: 'body2',
                            color: 'text.primary'
                          }} 
                        />
                      </ListItem>
                    ))}
                  </List>
                  
                  <Box sx={{ pt: 3, pb: 1 }}>
                    <Button
                      component={RouterLink}
                      to={plan.title === 'Enterprise' ? '/contact' : '/register'}
                      fullWidth
                      variant={plan.buttonVariant as 'contained' | 'outlined'}
                      color="primary"
                      size="large"
                      sx={{ 
                        py: 1.5,
                        borderRadius: '8px',
                        fontWeight: 600,
                        ...(plan.buttonVariant === 'contained' && {
                          bgcolor: '#2d8cff',
                          '&:hover': {
                            bgcolor: '#1a7aee'
                          }
                        }),
                        ...(plan.buttonVariant === 'outlined' && {
                          borderColor: '#1a365d',
                          color: '#1a365d',
                          '&:hover': {
                            borderColor: '#1a365d',
                            bgcolor: 'rgba(26,54,93,0.04)'
                          }
                        })
                      }}
                    >
                      {plan.buttonText}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
      
      {/* FAQ Section */}
      <Box sx={{ bgcolor: '#f8faff', py: { xs: 8, md: 10 } }}>
        <Container maxWidth="lg">
          <Typography 
            variant="h3" 
            component="h2" 
            fontWeight="700" 
            textAlign="center"
            sx={{ 
              mb: 2,
              color: '#1a365d',
              fontSize: { xs: '1.75rem', md: '2.5rem' }
            }}
          >
            Frequently Asked Questions
          </Typography>
          <Typography 
            variant="subtitle1" 
            textAlign="center" 
            color="text.secondary" 
            sx={{ 
              mb: 6, 
              maxWidth: '700px', 
              mx: 'auto',
              fontSize: { xs: '1rem', md: '1.1rem' }
            }}
          >
            Find answers to common questions about our pricing and plans.
          </Typography>
          
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Paper 
                elevation={0}
                sx={{ 
                  p: 4, 
                  height: '100%',
                  borderRadius: '16px',
                  border: '1px solid rgba(0,0,0,0.05)',
                }}
              >
                <Typography variant="h6" fontWeight="700" gutterBottom sx={{ color: '#1a365d' }}>
                  Can I upgrade or downgrade my plan?
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Yes, you can upgrade or downgrade your plan at any time. If you upgrade, you'll be charged the prorated difference. If you downgrade, you'll receive a prorated credit towards your next billing cycle.
                </Typography>
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Paper 
                elevation={0}
                sx={{ 
                  p: 4, 
                  height: '100%',
                  borderRadius: '16px',
                  border: '1px solid rgba(0,0,0,0.05)',
                }}
              >
                <Typography variant="h6" fontWeight="700" gutterBottom sx={{ color: '#1a365d' }}>
                  What happens after my free trial?
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  After your 14-day free trial ends, you'll be automatically switched to the Free plan unless you choose to upgrade to a paid plan. We'll send you a reminder before your trial ends.
                </Typography>
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Paper 
                elevation={0}
                sx={{ 
                  p: 4, 
                  height: '100%',
                  borderRadius: '16px',
                  border: '1px solid rgba(0,0,0,0.05)',
                }}
              >
                <Typography variant="h6" fontWeight="700" gutterBottom sx={{ color: '#1a365d' }}>
                  Do you offer discounts for non-profits or educational institutions?
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Yes, we offer special pricing for non-profit organizations, educational institutions, and students. Please contact our sales team for more information.
                </Typography>
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Paper 
                elevation={0}
                sx={{ 
                  p: 4, 
                  height: '100%',
                  borderRadius: '16px',
                  border: '1px solid rgba(0,0,0,0.05)',
                }}
              >
                <Typography variant="h6" fontWeight="700" gutterBottom sx={{ color: '#1a365d' }}>
                  What payment methods do you accept?
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  We accept all major credit cards (Visa, Mastercard, American Express, Discover) and PayPal. For Enterprise plans, we also offer invoicing options.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>
      
      {/* CTA Section */}
      <Container maxWidth="md" sx={{ py: { xs: 8, md: 10 } }}>
        <Box 
          sx={{ 
            textAlign: 'center',
            p: { xs: 4, md: 6 },
            borderRadius: '16px',
            background: 'linear-gradient(135deg, #1a365d 0%, #2d8cff 100%)',
            color: 'white',
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)'
          }}
        >
          <Typography 
            variant="h4" 
            fontWeight="700" 
            gutterBottom
            sx={{ 
              fontSize: { xs: '1.5rem', md: '2rem' }
            }}
          >
            Ready to Transform Your Legal Workflow?
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              mb: 4, 
              maxWidth: '600px', 
              mx: 'auto',
              opacity: 0.9
            }}
          >
            Join thousands of professionals who are saving time and reducing errors with Contract AI.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Button 
              variant="contained" 
              size="large"
              component={RouterLink}
              to="/register"
              sx={{ 
                bgcolor: '#6200ea',
                color: 'white',
                fontWeight: 600,
                px: 4,
                py: 1.5,
                borderRadius: '8px',
                '&:hover': {
                  bgcolor: '#7c4dff'
                }
              }}
            >
              Start Your Free Trial
            </Button>
            <Button 
              variant="outlined" 
              size="large"
              component={RouterLink}
              to="/contact"
              sx={{ 
                borderColor: 'white',
                color: 'white',
                fontWeight: 600,
                px: 4,
                py: 1.5,
                borderRadius: '8px',
                '&:hover': {
                  borderColor: 'white',
                  bgcolor: 'rgba(255,255,255,0.1)'
                }
              }}
            >
              Contact Sales
            </Button>
          </Box>
        </Box>
      </Container>
      
      <Footer />
    </Box>
  );
};

export default PricingPage;
